import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { TextareaUI } from "ui/textareaUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Save,
  Receipt,
  DollarSign,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { FullScreenLoading } from "components/ui/loader";

import {
  getApprovedTourOperators,
  createAdvanceReceipt,
  updateAdvanceReceipt,
  getAdvanceReceiptById,
  TourOperatorApi,
  postAdvanceReceipt,
  BaseCurrencyApi,
  getBaseCurrency,
} from "../../services/advanceReceiptService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

interface AdvanceReceiptFormPageProps {
  onNavigate: (page: string) => void;
  receiptId?: string;
  mode?: "create" | "edit";
}

type ReceiptAction = "DRAFT" | "POST" | "UPDATE";

export const AdvanceReceiptFormPage = ({
  onNavigate,
  receiptId,
  mode = "create",
}: AdvanceReceiptFormPageProps) => {
  const { setPageHeader } = usePageHeader();
  const { id } = useParams<{ id: string }>();
  const finalReceiptId = receiptId || id;
  const isEditMode = mode === "edit" && !!finalReceiptId;
  const [receiptStatus, setReceiptStatus] = useState<"DRAFT" | "POSTED">(
    "DRAFT",
  );
  const isViewMode = isEditMode && receiptStatus === "POSTED";
  const [searchParams] = useSearchParams();
  const pageFromList = searchParams.get("page");
  const [receiptNo, setReceiptNo] = useState(
    isEditMode ? "" : "Auto-generated",
  );
  const [receiptDate, setReceiptDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [tourOperatorId, setTourOperatorId] = useState("");
  const [tourOperatorName, setTourOperatorName] = useState("");
  const [paymentMode, setPaymentMode] = useState<string>("Cash");
  const [transactionRef, setTransactionRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [tourOperators, setTourOperators] = useState<TourOperatorApi[]>([]);
  const [tourOperatorSearch, setTourOperatorSearch] = useState("");
  const [baseCurrency, setBaseCurrency] = useState<BaseCurrencyApi | null>(
    null,
  );
  const [errors, setErrors] = useState<{
    tourOperator?: string;
    paymentMode?: string;
    description?: string;
    amount?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const { canAdd, canEdit, icon: ScreenIcon } = useScreenPermission();

  // useEffect(() => {
  //   const loadTourOperators = async () => {
  //     try {
  //       const res = await getTourOperators();
  //       setTourOperators(res);
  //     } catch {
  //       toast.error("Failed to load Tour Operators");
  //     }
  //   };

  //   loadTourOperators();
  // }, []);

  useEffect(() => {
    setPageHeader({
      title: isViewMode
        ? "View Advance Receipt"
        : isEditMode
          ? "Edit Advance Receipt"
          : "Create Advance Receipt",

      icon: <ScreenIcon className="h-6 w-6" />,

      onBack: () =>
        onNavigate(
          pageFromList
            ? `/advance-receipts?page=${pageFromList}`
            : "/advance-receipts"
        ),
    });

    return () => setPageHeader(null);
  }, [
    isViewMode,
    isEditMode,
    pageFromList,
    onNavigate,
    ScreenIcon,
    setPageHeader,
  ]);

  useEffect(() => {
    if (!isEditMode && !canAdd) {
      toast.error("You do not have permission to create receipts");
      onNavigate("advance-receipts");
    }

    if (isEditMode && !canEdit) {
      toast.error("You do not have permission to edit receipts");
      onNavigate("advance-receipts");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, canAdd, canEdit]);

  useEffect(() => {
    getTourOperatorData();
  }, []);

  const getTourOperatorData = async () => {
    try {
      setLoading(true);
      const res = await getApprovedTourOperators();
      console.log("Approved Tour Operators:", res);
      setTourOperators(res);
    } catch (err) {
      console.error("Error fetching approved tour operators:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || !finalReceiptId) return;
    const loadReceipt = async () => {
      try {
        setLoading(true);

        const data = await getAdvanceReceiptById(Number(finalReceiptId));
        setReceiptNo(data.receiptNo);
        setReceiptDate(data.receiptDate.split("T")[0]);
        setPaymentMode(data.paymentMode);
        setTransactionRef(data.transactionRef ?? "");
        setRemarks(data.remarks ?? "");
        setDescription(data.description ?? "");
        setAmount(data.amount);
        setTourOperatorName(data.tourOperatorName);
        setReceiptStatus(data.status ?? "DRAFT");
      } catch {
        toast.error("Failed to load receipt details");
      } finally {
        setLoading(false);
      }
    };

    loadReceipt();
  }, [isEditMode, finalReceiptId]);

  useEffect(() => {
    const loadBaseCurrency = async () => {
      try {
        setLoading(true);
        const res = await getBaseCurrency();
        setBaseCurrency(res);
      } catch {
        toast.error("Failed to load base currency");
      } finally {
        setLoading(false);
      }
    };

    loadBaseCurrency();
  }, []);

  const filteredTourOperators = tourOperators.filter(
    (to) =>
      `${to.firstName} ${to.middleName ?? ""} ${to.lastName}`
        .toLowerCase()
        .includes(tourOperatorSearch.toLowerCase()) ||
      to.itsNumber.includes(tourOperatorSearch) ||
      to.officeName.toLowerCase().includes(tourOperatorSearch.toLowerCase()),
  );

  const selectedTourOperator = tourOperators.find(
    (to) => to.tourOperatorId === Number(tourOperatorId),
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedTourOperatorName = selectedTourOperator
    ? `${selectedTourOperator.firstName} ${selectedTourOperator.middleName ?? ""} ${selectedTourOperator.lastName}`
    : "";

  const navigateBackToList = () => {
    onNavigate(
      pageFromList
        ? `/advance-receipts?page=${pageFromList}`
        : "advance-receipts",
    );
  };

  const TRANSACTION_REFERENCE_REGEX = /^[A-Z0-9 ]*$/;
  const REMARKS_REGEX = /^[A-Za-z ]*$/;
  const DESCRIPTION_REGEX = /^[A-Za-z ]*$/;

  const handleSubmit = async (action: ReceiptAction) => {
    if (isViewMode) return;
    const newErrors: Partial<typeof errors> = {};
    if (!paymentMode) newErrors.paymentMode = "Please select Payment Mode";
    if (!description.trim()) {
      newErrors.description = "Description is required";
    } else if (!DESCRIPTION_REGEX.test(description.trim())) {
      newErrors.description =
        "Description can contain only letters, numbers and spaces (maximum 50 characters).";
    }
    if (!isEditMode) {
      if (!tourOperatorId)
        newErrors.tourOperator = "Please select a Tour Operator";
      if (amount <= 0) newErrors.amount = "Amount must be greater than 0";
    }
    if (amount <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    if (transactionRef && !TRANSACTION_REFERENCE_REGEX.test(transactionRef)) {
      toast.error(
        "Transaction Reference can contain only uppercase letters, numbers and spaces.",
      );
      return;
    }

    if (remarks && !REMARKS_REGEX.test(remarks)) {
      toast.error("Remarks can contain only letters and spaces.");
      return;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors as typeof errors);
      return;
    }
    try {
      setLoading(true);
      const userId = Number(localStorage.getItem("userId"));
      const isoDate = new Date(receiptDate).toISOString();
      const commonPayload = {
        paymentMode,
        transactionRef: transactionRef || null,
        remarks: remarks || null,
        description,
      };
      let receiptId: number;
      if (!isEditMode) {
        const created = await createAdvanceReceipt({
          ...commonPayload,
          receiptDate: isoDate,
          tourOperatorId: Number(tourOperatorId),
          currency: baseCurrency?.currencyCode ?? "USD",
          amount,
          createdBy: userId,
        });
        receiptId = created.receiptId;
        setReceiptNo(created.receiptNo);
      } else {
        receiptId = Number(finalReceiptId);
        await updateAdvanceReceipt({
          ...commonPayload,
          receiptId,
          receiptDate: isoDate,
          amount,
        });
      }
      if (action === "POST") {
        await postAdvanceReceipt(receiptId);
        setReceiptStatus("POSTED");
      }
      const actionText =
        action === "POST"
          ? "posted"
          : isEditMode
            ? "updated"
            : "saved as draft";
      toast.success(`Advance receipt ${actionText} successfully`);

      navigateBackToList();
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error("Failed to save advance receipt");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = amount;
  const handleAmountChange = (value: string) => {
    const parsed = Number(value);
    setAmount(isNaN(parsed) ? 0 : parsed);
    setErrors((prev) => ({ ...prev, amount: undefined }));
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* HEADER */}
        {/* <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              onNavigate(
                pageFromList
                  ? `/advance-receipts?page=${pageFromList}`
                  : "advance-receipts",
              )
            }
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1
              style={{ color: "#5B9BD5" }}
              className="flex items-center gap-2"
            >
              <ReceiptCentIcon className="w-6 h-6" />
              {isViewMode
                ? "View Advance Receipt"
                : isEditMode
                  ? "Edit Advance Receipt"
                  : "Create Advance Receipt"}
            </h1>

            <p className="text-gray-500">
              {isViewMode
                ? `Viewing receipt details - Receipt No: ${receiptNo}`
                : isEditMode
                  ? `Update receipt details - Receipt No: ${receiptNo}`
                  : "Record advance payment from Tour Operator"}
            </p>
          </div>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Details */}
            <Card
              className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
              style={{ gap: "0px" }}
            >
              <CardHeader className="border-b border-gray-100 pb-4">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="flex items-center gap-2"
                >
                  <Receipt className="w-5 h-5" />
                  Receipt Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <InputFieldset
                      label="Receipt Number"
                      value={receiptNo}
                      disabled
                      className="bg-gray-100 border-gray-300 mt-1.5 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <InputFieldset
                      label="Receipt Date"
                      type="date"
                      value={receiptDate}
                      disabled
                      onChange={(e) => setReceiptDate(e.target.value)}
                      className={`border-gray-300 mt-1.5 ${isEditMode ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    />
                  </div>
                </div>

                <div>
                  {isEditMode ? (
                    <InputFieldset
                      label="Tour Operator"
                      value={tourOperatorName}
                      disabled
                      className="bg-gray-100 border-gray-300 mt-1.5 cursor-not-allowed"
                    />
                  ) : (
                    <SelectFieldset label="Tour Operator" required>
                      <Select
                        value={tourOperatorId}
                        onValueChange={(value) => {
                          setTourOperatorId(value);
                          setErrors((prev) => ({
                            ...prev,
                            tourOperator: undefined,
                          }));
                        }}
                      >
                        <SelectTriggerUI className="border-gray-300 mt-1.5">
                          <SelectValue placeholder="Select Tour Operator">
                            {filteredTourOperators.find(
                              (to) =>
                                to.tourOperatorId.toString() === tourOperatorId,
                            )?.officeName || "Select Tour Operator"}
                          </SelectValue>
                        </SelectTriggerUI>
                        <SelectContent>
                          <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                            <div className="relative">
                              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <Input
                                className="w-full pl-9 pr-3 py-2 border-gray-300 rounded-md focus:outline-none focus:border-[#5B9BD5] focus:ring-1 focus:ring-[#5B9BD5]"
                                placeholder="Search by name, ITS, or office..."
                                value={tourOperatorSearch}
                                onChange={(e) =>
                                  setTourOperatorSearch(e.target.value)
                                }
                                onKeyDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {filteredTourOperators.length > 0 ? (
                              [...filteredTourOperators]
                                .sort((a, b) =>
                                  a.officeName.localeCompare(
                                    b.officeName,
                                    undefined,
                                    {
                                      sensitivity: "base",
                                    },
                                  ),
                                )
                                .map((to) => (
                                  <SelectItem
                                    key={to.tourOperatorId}
                                    value={to.tourOperatorId.toString()}
                                  >
                                    <div className="flex flex-col py-1">
                                      <span className="font-medium">
                                        {to.officeName}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))
                            ) : (
                              <div className="px-2 py-6 text-center text-sm text-gray-500">
                                No tour operators found
                              </div>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  )}
                  {errors.tourOperator && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.tourOperator}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <SelectFieldset label="Payment Mode" required>
                      <Select
                        value={paymentMode}
                        disabled={isViewMode}
                        onValueChange={(value) => {
                          setPaymentMode(value);
                          setErrors((prev) => ({
                            ...prev,
                            paymentMode: undefined,
                          }));
                        }}
                      >
                        <SelectTriggerUI className="border-gray-300">
                          <SelectValue placeholder="Select Payment Mode" />
                        </SelectTriggerUI>

                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Cheque">Cheque</SelectItem>
                          <SelectItem value="Bank Transfer">
                            Bank Transfer
                          </SelectItem>
                          <SelectItem value="UPI">UPI</SelectItem>
                          <SelectItem value="Credit/Debit Card">
                            Credit/Debit Card
                          </SelectItem>
                          <SelectItem value="Refund">Refund</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>

                    {errors.paymentMode && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.paymentMode}
                      </p>
                    )}
                  </div>

                  <div>
                    <InputFieldset
                      label="Transaction Reference"
                      value={transactionRef}
                      disabled={isViewMode}
                      onChange={(e) => {
                        const value = e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9 ]/g, "");

                        setTransactionRef(value);
                      }}
                      placeholder="Cheque No / Transaction ID"
                      className="border-gray-300"
                    />
                  </div>
                </div>

                <div>
                  <SelectFieldset label="Remarks">
                    <TextareaUI
                      value={remarks}
                      disabled={isViewMode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                        setRemarks(value);
                      }}
                      placeholder="Additional notes or comments"
                      className="border-gray-300 mt-1.5 min-h-[80px]"
                    />
                  </SelectFieldset>
                </div>
              </CardContent>
            </Card>

            {/* Amount Entries */}
            <Card
              className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
              style={{ gap: "0px", marginTop: "10px" }}
            >
              <CardHeader className="border-b border-gray-100 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2"
                  >
                    <DollarSign className="w-5 h-5" />
                    Amount Entries
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <InputFieldset
                        label="Description"
                        required
                        value={description}
                        disabled={isViewMode}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^A-Za-z0-9 ]/g, "")
                            .slice(0, 50);

                          setDescription(value);

                          setErrors((prev) => ({
                            ...prev,
                            description: undefined,
                          }));
                        }}
                        placeholder="e.g., Advance for Group Visa"
                        className="border-gray-300 mt-1"
                      />
                      {errors.description && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.description}
                        </p>
                      )}
                    </div>
                    <div>
                      <InputFieldset
                        label={`Amount (${baseCurrency?.currencyCode ?? "USD"})`}
                        type="number"
                        value={amount || ""}
                        disabled={isViewMode}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className={`border-gray-300 mt-1 ${
                          isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                      />

                      {errors.amount && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.amount}
                        </p>
                      )}
                      {errors.amount && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.amount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <Card
                className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-lg"
                style={{ gap: "0px" }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-white">Receipt Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditMode && (
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-white/80">Receipt Number</span>
                      <span className="text-lg text-white">{receiptNo}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-white/80">Total Amount</span>
                    <span className="text-2xl text-white">
                      ${(totalAmount ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-white/70">
                      {isEditMode
                        ? "This amount represents the total in the Tour Operator's wallet."
                        : "This amount will be credited to the Tour Operator's wallet upon receipt creation."}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {!isEditMode && (
                <Button
                  onClick={() => handleSubmit("DRAFT")}
                  disabled={!!errors.amount}
                  className="w-full text-white shadow-lg hover:opacity-90 h-12"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save As Draft
                </Button>
              )}
              {!isViewMode && (
                <Button
                  onClick={() => handleSubmit(isEditMode ? "UPDATE" : "POST")}
                  disabled={!!errors.amount}
                  className="w-full text-white shadow-lg hover:opacity-90 h-12"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? "Update Receipt" : "Post Receipt"}
                </Button>
              )}

              {!isViewMode && (
                <Button
                  variant="outline"
                  onClick={navigateBackToList}
                  className="w-full border-gray-300"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
